/**
 * <pet-feed-result> — Shopify web component
 *
 * After the user submits a pet weight via <pet-feed-input>, this component:
 *  1. Calculates the optimal bag size + quantity to cover 28 days of feeding,
 *     scoped to the currently-selected flavor/protein (non-size options).
 *  2. Shows a green recommendation bar: "We recommend N [size] bags every 4 weeks".
 *  3. Re-calculates automatically when the shopper picks a different variant.
 *  4. Provides a "Buy now" button that adds the recommendation to cart as a
 *     4-week subscription (selling plan detected from the embedded JSON).
 *
 * Data is injected server-side via a <script type="application/json" data-pet-feed-variants>
 * block in the paired pet-feed-result.liquid snippet.
 */
class PetFeedResult extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._abortController = new AbortController();
    const { signal } = this._abortController;

    // Parse embedded JSON: { sellingPlanId, variants: { [id]: { total_kcal_bag, available, variant_title, options } } }
    try {
      const dataEl = this.querySelector('[data-pet-feed-variants]');
      const parsed = dataEl ? JSON.parse(dataEl.textContent) : {};
      this._sellingPlanId = parsed.sellingPlanId ?? null;
      this._variants = parsed.variants ?? {};
      console.log('[pet-feed-result] variants:', this._variants);
    } catch {
      this._sellingPlanId = null;
      this._variants = {};
    }

    this._display = this.querySelector('[data-pet-feed-display]');
    this._recommendationEl = this.querySelector('[data-pet-feed-recommendation]');
    this._buyNowBtn = this.querySelector('[data-pet-feed-buy-now]');

    if (this._display) this._display.hidden = true;

    const namespace = this.getAttribute('event-namespace') || 'pet-feed';

    // Weight submitted by the shopper
    document.addEventListener(`${namespace}:weight-updated`, (e) => {
      this._lastWeight = e.detail.weightLbs;
      this._lastPetType = e.detail.petType;
      this._render();
    }, { signal });

    // Manual size change — hide recommendation and clear the weight input
    document.addEventListener('change', (e) => {
      const el = e.target;
      if (el.type !== 'radio' || !el.hasAttribute('data-option-value-id')) return;
      if (el.dataset.petFeedAutoSelect) return; // our own click, ignore
      if (!this._lastWeight) return; // nothing showing, nothing to clear
      if (this._display) this._display.hidden = true;
      this._lastWeight = null;
      const input = document.querySelector('[data-weight-input]');
      if (input) input.value = '';
    }, { signal });

    // "Buy now" button
    this._buyNowBtn?.addEventListener('click', () => this._buyNow(), { signal });
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._initialized = false;
  }

  // Build sorted candidate list from variants with kcal data.
  // requireAvailable=true (default) skips OOS variants; false includes them for display-only mode.
  // All variants on the page share the same flavor (combined listing products), so no flavor filtering needed.
  _buildCandidates(dailyKcal, requireAvailable = true) {
    return Object.entries(this._variants)
      .map(([id, data]) => {
        if (requireAvailable && !data.available) return null;
        if (data.total_kcal_bag == null || data.total_kcal_bag <= 0) return null;
        const bagsNeeded = Math.ceil((28 * dailyKcal) / data.total_kcal_bag);
        return { id, data, bagsNeeded };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.bagsNeeded !== b.bagsNeeded) return a.bagsNeeded - b.bagsNeeded;
        const need = 28 * dailyKcal;
        return (a.bagsNeeded * a.data.total_kcal_bag - need) - (b.bagsNeeded * b.data.total_kcal_bag - need);
      });
  }

  _render() {
    const weight = this._lastWeight;
    if (!weight || weight <= 0) return;

    const petType = this._lastPetType || this.getAttribute('pet-type') || 'dog';
    const config = window.PetFeedConfig;

    if (!config?.[petType]) {
      if (this._display) this._display.hidden = true;
      console.warn('[pet-feed-result] window.PetFeedConfig missing or has no entry for petType:', petType,
        '— make sure the pet-feed-calculator block is on the same page.');
      return;
    }

    const range = config[petType].find((r) => weight >= r.min && weight < r.max);
    if (!range) {
      if (this._display) this._display.hidden = true;
      console.warn('[pet-feed-result] Weight', weight, 'lbs is not covered by PetFeedConfig[' + petType + '].');
      return;
    }

    const dailyKcal = weight * range.kcal_per_lb;

    // Build all candidates regardless of availability — sorted by optimal fit.
    const allCandidates = this._buildCandidates(dailyKcal, false);

    if (!allCandidates.length) {
      const variantSummary = Object.entries(this._variants).map(([id, d]) =>
        `${id}: kcal=${d.total_kcal_bag} avail=${d.available}`
      ).join(', ');
      console.warn(
        '[pet-feed-result] No variants with total_kcal_bag found.',
        '\nActive variant:', this._activeVariantId,
        '\nVariant summary:', variantSummary,
        '\nFull data:', this._variants,
        '\nSet the `theme.total_kcal_bag` (integer) metafield on each variant.',
      );
      if (this._display) this._display.hidden = true;
      return;
    }

    const optimal = allCandidates[0];
    let best = optimal;
    let oosMode = false;

    if (!optimal.data.available) {
      // Optimal size is OOS — try the next in-stock size up (smallest bag with more kcal).
      const nextUp = allCandidates
        .filter((c) => c.data.available && c.data.total_kcal_bag > optimal.data.total_kcal_bag)
        .sort((a, b) => a.data.total_kcal_bag - b.data.total_kcal_bag)[0];

      if (nextUp) {
        best = nextUp;
      } else {
        // No larger in-stock size — show the optimal OOS size so shopper can use Notify Me.
        oosMode = true;
      }
    }
    this._recommendedVariantId = best.id;
    this._recommendedQuantity = best.bagsNeeded;

    // Extract size label from variant title ("Beef / 25 lb." → "25 lb.", "25 lb." → "25 lb.")
    const sizeLabel = best.data.variant_title.split(' / ').pop().trim();
    const bagWord = best.bagsNeeded === 1 ? 'bag' : 'bags';

    const qtyDisplay = String(best.bagsNeeded);

    if (this._recommendationEl) {
      this._recommendationEl.innerHTML =
        `We recommend <span class="pet-feed-result__qty">${qtyDisplay}</span> <strong class="pet-feed-result__size">${sizeLabel} ${bagWord}</strong> every 4 weeks`;
    }

    if (this._display) {
      this._display.hidden = false;
      // Reset any error styling from a previous OOS/error render
      this._display.style.background = '';
      this._display.style.borderColor = '';
    }

    // Hide Buy Now when OOS — shopper can't purchase, but should still see the size recommendation
    // and be able to use "Notify me" or a 3rd-party retailer link on the selected size.
    // Use display style directly: the CSS class sets display:inline-flex which overrides [hidden].
    if (this._buyNowBtn) {
      this._buyNowBtn.hidden = oosMode;
      this._buyNowBtn.style.display = oosMode ? 'none' : '';
    }

    this._selectVariantInPicker(this._recommendedVariantId);
  }

  // Clicks the size radio whose data-variant-id matches variantId, if it isn't already checked.
  // Scoped to size options (radio value contains "lb") so flavor radios — which navigate to a
  // different combined-listing product — are never accidentally triggered.
  _selectVariantInPicker(variantId) {
    if (!variantId) return;
    const radio = document.querySelector(`input[type="radio"][data-variant-id="${variantId}"]`);
    if (radio && !radio.checked) {
      radio.dataset.petFeedAutoSelect = '1';
      radio.click();
      delete radio.dataset.petFeedAutoSelect;
    }
  }

  /**
   * Adds the recommended variant + quantity to cart with a 4-week subscription plan.
   * Mirrors product-form.js exactly: requests section re-renders in the same POST,
   * then dispatches cart:update with the sections data so cart-drawer opens and
   * cart-items-component morphs its content without a second round-trip.
   */
  async _buyNow() {
    if (!this._recommendedVariantId) return;

    const btn = this._buyNowBtn;
    if (btn) btn.disabled = true;

    // Collect cart-items-component section IDs so the server renders fresh HTML
    // for all cart panels in one shot (same approach as product-form.js).
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const payload = {
      items: [
        {
          id: parseInt(this._recommendedVariantId, 10),
          quantity: this._recommendedQuantity || 1,
          ...(this._sellingPlanId != null ? { selling_plan: this._sellingPlanId } : {}),
        },
      ],
      ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
    };

    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const addData = await addRes.json();

      if (addData.status) {
        console.error('[pet-feed-result] Cart add error:', addData.description || addData.message);
        return;
      }

      // Fetch updated cart for item_count (used by cart-drawer for the badge announcement)
      const cart = await fetch('/cart.js').then((r) => r.json());

      // Dispatch cart:update matching CartAddEvent's exact structure (instance property,
      // not CustomEvent detail slot). cart-drawer-component opens on this; cart-items-component
      // reads event.detail.data.sections[sectionId] to morph its content.
      const cartEvent = new Event('cart:update', { bubbles: true });
      cartEvent.detail = {
        resource: cart,
        sourceId: this.id || 'pet-feed-result',
        data: {
          source: 'pet-feed-calculator',
          variantId: String(this._recommendedVariantId),
          itemCount: this._recommendedQuantity || 1,
          sections: addData.sections ?? {},
        },
      };
      document.dispatchEvent(cartEvent);
    } catch (err) {
      console.error('[pet-feed-result] Buy now failed:', err);
    } finally {
      if (btn) btn.disabled = false;
    }
  }
}

if (!customElements.get('pet-feed-result')) {
  customElements.define('pet-feed-result', PetFeedResult);
}
